import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/ui/notify";
import { useAuthStore } from "@/stores/auth.store";
import { useLines } from "@/features/lines/hooks";
import { suggestLineCode } from "@/features/lines/code-suggestion";
import { useLineDraftState } from "@/features/lines/draft/useLineDraftState";
import { saveLineAtomicFull } from "@/features/lines/draft/saveDraftAtomic";
import { validateDraft } from "@/features/lines/draft/validateDraft";
import { autoLayoutDraft } from "@/features/lines/draft/autoLayoutDraft";
import { useDraftIdempotencyKey } from "@/features/lines/draft/useDraftIdempotencyKey";
import { useBeforeUnloadGuard } from "@/features/lines/draft/useBeforeUnloadGuard";
import * as sessionDraft from "@/features/lines/draft/sessionDraftStorage";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { ProductionLineCanvas } from "@/features/lines/canvas/ProductionLineCanvas";
import { DRAFT_LINE_SENTINEL } from "@/features/lines/canvas/draft-canvas-adapter";
import { LineHeaderForm } from "@/features/lines/editor/components/LineHeaderForm";
import { MachineSidebar } from "@/features/machine-sidebar/MachineSidebar";
import { useEditorAggregate } from "./helpers";

export function CreationModeView() {
  const navigate = useNavigate();
  const me = useAuthStore((s) => s.user)!;
  const linesQuery = useLines();
  const { data: lines = [] } = linesQuery;

  const { draft, setHeader, reset, store } = useLineDraftState();
  const idempotencyKey = useDraftIdempotencyKey();
  const aggregate = useEditorAggregate();

  const [sidebarMachineId, setSidebarMachineId] = useState<string | null>(null);

  useBeforeUnloadGuard(draft.dirty);

  useEffect(() => {
    const stored = sessionDraft.load(me.id, me.organizationId);
    if (stored) {
      store.getState().loadFromSession(stored.draft);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsub = store.subscribe((state) => {
      if (!state.dirty) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        sessionDraft.save(
          me.id,
          me.organizationId,
          {
            header: state.header,
            canvas: state.canvas,
            machines: state.machines,
            dirty: state.dirty,
          },
          idempotencyKey
        );
      }, 300);
    });
    return () => {
      if (timer) clearTimeout(timer);
      unsub();
    };
  }, [store, me.id, me.organizationId, idempotencyKey]);

  // Impacto mínimo (só sugestão de código), mas em erro não sabemos os códigos já
  // usados: melhor não sugerir nada do que sugerir um código possivelmente duplicado.
  const suggestion = linesQuery.isError
    ? undefined
    : suggestLineCode(lines.map((l) => ({ code: l.name })));

  const validation = useMemo(() => {
    const laidOut = autoLayoutDraft(draft);
    return validateDraft(laidOut);
  }, [draft]);

  async function onSaveOverride() {
    if (!validation.ok) {
      toast.error("Há pendências de configuração.", {
        description: `${validation.errors.length} erro(s) — verifique cabeçalho, máquinas e canvas.`,
      });
      throw new Error("Validation failed");
    }
    try {
      const laidOut = autoLayoutDraft(draft);
      const result = await saveLineAtomicFull(laidOut, idempotencyKey);
      toast.success("Linha criada com sucesso.");
      store.getState().applyIdMap(result.idMap);
      sessionDraft.clear(me.id, me.organizationId);
      reset();
      aggregate?.reset();
      navigate(`/lines/${result.lineId}/edit`, { replace: true });
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao criar linha.", { description: msg });
      throw err;
    }
  }

  return (
    <section className="flex h-full flex-col gap-3">
      <LineHeaderForm
        mode="creation"
        values={draft.header}
        onChange={(patch) => setHeader(patch)}
        nameSuggestion={suggestion}
      />
      <div className="min-h-[520px] flex-1">
        <ProductionLineCanvas
          lineId={DRAFT_LINE_SENTINEL}
          mode="edit"
          height={520}
          onSaveOverride={onSaveOverride}
          externalCanSave={aggregate ? aggregate.canSave : validation.ok}
          onOpenSidebar={(node) => {
            const mid = (node.data?.machineId as string | undefined) ?? null;
            if (mid) setSidebarMachineId(mid);
          }}
          onCreateNewMachine={(basic) => {
            const tmpMachineId = store.getState().addMachine(basic);
            const newNode = store
              .getState()
              .canvas.nodes.find((n) => n.machineTmpId === tmpMachineId);
            if (newNode) {
              window.dispatchEvent(
                new CustomEvent("opus:editor:select-node", {
                  detail: { nodeId: newNode.tmpId },
                })
              );
            }
          }}
        />
      </div>

      {sidebarMachineId ? (
        <MachineSidebar
          machineId={sidebarMachineId}
          lineId={DRAFT_LINE_SENTINEL}
          mode="edit"
          context="line-edit"
          onClose={() => setSidebarMachineId(null)}
          onMachineChange={(event) => {
            if (event.type === "removed") setSidebarMachineId(null);
          }}
        />
      ) : null}
    </section>
  );
}
